import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  ScrollView,
} from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { QuickReplyItemForm, QuickReplyItemFormData } from '../components/quick-replies';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface QuickReplyItemCreateScreenProps {
  setId?: string;
}

export const QuickReplyItemCreateScreen: React.FC<QuickReplyItemCreateScreenProps> = ({ setId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [saving, setSaving] = useState(false);
  const [availableRepliesSets, setAvailableRepliesSets] = useState<Array<{ _id: string; name: string }>>([]);
  const formSubmitRef = useRef<(() => void) | null>(null);

  const actualSetId = setId;

  useEffect(() => {
    loadAvailableRepliesSets();
  }, []);

  const loadAvailableRepliesSets = async () => {
    try {
      const response = await apiService.getQuickReplySets({
        limit: 1000, // Load all sets
        skip: 0,
      });
      if (response.statusCode === 1000 && response.data?.quickReplySets) {
        // Filter out the current set to prevent self-reference
        const filtered = response.data.quickReplySets
          .filter((set: any) => set._id !== actualSetId)
          .map((set: any) => ({ _id: set._id, name: set.name }));
        setAvailableRepliesSets(filtered);
      }
    } catch (error) {
      console.error('Error loading replies sets:', error);
    }
  };

  const handleSubmit = async (itemData: QuickReplyItemFormData) => {
    try {
      setSaving(true);

      // Create standalone item (no setId required)
      const response = await apiService.createQuickReplyItem({
        displayText: itemData.displayText,
        type: itemData.type,
        config: itemData.config || {},
        displayOrder: itemData.displayOrder || 0,
        isActive: itemData.isActive !== undefined ? itemData.isActive : true,
        icon: itemData.icon,
        bodyText: itemData.bodyText,
        setIds: itemData.setIds || [],
      });

      if (response.statusCode === 1000) {
        // 'flow' type items don't trigger their flow via the item's own config — that's
        // handled server-side entirely through the existing reply_item-scoped FlowAttachment
        // mechanism (already wired end-to-end: evaluateForTarget('reply_item', itemId, ...) is
        // called on every button tap before the rule engine). So creating a 'flow' item also
        // needs to create the matching attachment, using the item's now-known id.
        const newItemId = response.data?._id;
        if (itemData.type === 'flow' && itemData.config?.flowId && newItemId) {
          try {
            await apiService.createFlowAttachment({
              flowId: itemData.config.flowId,
              scope: 'reply_item',
              attachedTo: newItemId,
            });
          } catch (attachError: any) {
            console.error('Error attaching flow to item:', attachError);
            showNotification('error', 'Warning', 'Item was created, but attaching the flow failed. Edit the item to try again.');
          }
        }
        showNotification('success', 'Success', 'Item created successfully.');
        if (Platform.OS === 'web') {
          window.history.back();
        } else {
          // Navigate back to items list or set items screen if setId was provided
          if (actualSetId) {
            navigateTo('quick-reply-set-items', actualSetId);
          } else {
            navigateTo('quick-reply-items');
          }
        }
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to create item';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error creating item:', error);
      showNotification('error', 'Error', error.message || 'Failed to create item');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      if (actualSetId) {
        navigateTo('quick-reply-set-items', actualSetId);
      } else {
        navigateTo('quick-replies');
      }
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent="empty"
      formHeader={
        <FormHeader
          title="New Item"
          subtitle="Add a new quick reply item to use across sets"
          onBack={handleCancel}
          actions={[
            {
              icon: 'checkmark',
              onPress: () => formSubmitRef.current?.(),
              primary: true,
              loading: saving,
              disabled: saving,
            },
          ]}
        />
      }
    >
      <View style={styles.wrapper}>
        <ScrollView
          style={styles.container}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.formContainer}>
            <QuickReplyItemForm
              onSubmit={handleSubmit}
              submitLabel="Create Item"
              loading={saving}
              availableRepliesSets={availableRepliesSets}
              hideActions={true}
              submitRef={formSubmitRef}
            />
          </View>
        </ScrollView>
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    paddingBottom: spacing.xl,
  },
  formContainer: {
    padding: spacing.md,
  },
});

